import React, { useCallback, useMemo } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  CssBaseline,
  createMuiTheme,
  ThemeProvider,
  Box,
  IconButton,
} from '@material-ui/core';
import { NavLink, Router, useHistory } from 'react-router-dom';
import history from '@/history';
import Routes from '@/routes';
import { useAuth0 } from '@cmr/react-auth0';
import { Loading, NoAccess, UpdateNotify, AppBar } from '@cmr/react-ui-common';
import { ClientsProvider } from '@cmr/react-clients-api';
import { green } from '@material-ui/core/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AppBarWrapper: React.FC = () => {
  const history = useHistory();

  const onSearch = useCallback(
    (value: string) => history.push('/claims/search/' + value),
    [history],
  );

  return (
    <AppBar
      appName='Invoicing'
      color={green['900']}
      showSearchBox
      searchBoxProps={{
        clearOnSearch: true,
        onSearch,
      }}
    >
      <NavLink to='/print-batches'>
        <IconButton aria-label='Print Batches'>
          <FontAwesomeIcon icon={['fad', 'print']} />
        </IconButton>
      </NavLink>
    </AppBar>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, loading, unauthorized } = useAuth0();

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          primary: { main: green['900'] },
          secondary: { main: '#666666' },
          type: 'dark',
        },
      }),
    [],
  );

  if (unauthorized) return <NoAccess theme={theme} displayName='Invoicing' />;

  return (
    <ClientsProvider
      endpoint={process.env.REACT_APP_CLIENTS_ENDPOINT!}
      loadOnInit
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <UpdateNotify />
        {(loading || !isAuthenticated) && <Loading />}
        {!loading && isAuthenticated && (
          <Router history={history}>
            <Box height='100vh' display='flex' flexDirection='column'>
              <AppBarWrapper />
              <Routes />
            </Box>
          </Router>
        )}
      </ThemeProvider>
    </ClientsProvider>
  );
};

export default process.env.NODE_ENV === 'development' ? hot(App) : App;
